import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import errorHandler from "../../../utils/errorHandler";
import { toast } from "react-toastify";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import BTNLoader from "../../../lib/components/BTNLoader";
import * as Yup from "yup";
import organizationservice from "../../../service/organizationservice";
import AppSelectField from "../../../lib/components/AppSelectField";
import NumberFormatCustom from "../../../lib/components/CurrencyFormat";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const schema = Yup.object({
  planId: Yup.string().required(),
  billingInterval: Yup.string().required(),
  amount: Yup.number().typeError("Please enter a valid amount").required(),
  paymentDate: Yup.date().typeError("Please enter a valid payment date").required(),
});

const defaultValues = {
  planId: "",
  billingInterval: "",
  amount: "",
  paymentDate: null,
};
const UpdatePayment = ({ isOpen, onClose, refetch, organization, payment }) => {
  const formHook = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: defaultValues,
  });

  const errors = formHook.formState.errors;
  const control = formHook.control;
  const handleSubmit = formHook.handleSubmit;
  const reset = formHook.reset;

  const plansQuery = useQuery({
    retry: false,
    enabled: isOpen,
    queryKey: ["subscription-plans"],
    queryFn: organizationservice.getSubscriptionPlans,
  });

  const mutation = useMutation({
    mutationFn: (data) => organizationservice.updateInvoice(data),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      toast.success("Invoice payment updated successfully.");
      refetch();
      onClose();
    },
  });

  async function save(data) {
    mutation.mutate({
      amount: data?.amount,
      paymentDate: data?.paymentDate,
      organizationId: organization?.id,
      paymentId: payment?.paymentId,
    });
  }
  const { isPending } = mutation;

  const { data } = plansQuery;
  const plans = data?.data?.data || [];

  React.useEffect(() => {
    if (!organization) return;
    reset({
      planId: organization?.subscription?.planId,
      billingInterval: organization?.subscription?.billingInterval,
      amount: payment?.amount ?? "",
      paymentDate: payment?.paidAt ?? null,
    });
  }, [isOpen, organization]);

  return (
    <React.Fragment>
      <Dialog
        open={isOpen}
        maxWidth="sm"
        PaperProps={{
          className: "w-100",
          component: "form",
          onSubmit: handleSubmit(save),
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
          <div className="fs-5 fw-medium">Edit Payment</div>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>
        <Divider className="bg-secondary-subtle" />
        <DialogContent className="h-100 overflow-auto">
          <div className="mb-4">
            <label htmlFor="" className="d-block">
              Subscription Plan <span className="text-danger">*</span>
            </label>
            <Controller
              name="planId"
              control={control}
              render={({ field }) => (
                <AppSelectField
                  disabled
                  value={field.value}
                  placeholder="Select Plan"
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["planId"]?.message}
                  error={!!errors["planId"]}
                  items={plans?.map((item) => ({
                    label: item?.name,
                    value: item?.id,
                  }))}
                />
              )}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="" className="d-block">
              Period<span className="text-danger">*</span>
            </label>
            <Controller
              name="billingInterval"
              control={control}
              render={({ field }) => (
                <AppSelectField
                  disabled
                  value={field.value}
                  placeholder="Select Period"
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["billingInterval"]?.message}
                  error={!!errors["billingInterval"]}
                  items={[
                    { label: "Monthly", value: "MONTHLY" },
                    { label: "Quarterly", value: "QUARTERLY" },
                    { label: "Bi-Annually", value: "BIANNUALLY" },
                    { label: "Annually", value: "ANNUALLY" },
                  ]}
                />
              )}
            />
          </div>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className="mb-4">
                <label htmlFor="" className="d-block">
                  Payment Amount <span className="text-danger">*</span>
                </label>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["amount"]?.message}
                      error={!!errors["amount"]}
                      InputProps={{
                        className: "bg-white",
                        inputComponent: NumberFormatCustom,
                        startAdornment: (
                          <InputAdornment position="start">
                            <span>₦</span>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="mb-4">
                <label htmlFor="" className="d-block">
                  Payment Date <span className="text-danger">*</span>
                </label>
                <Controller
                  name="paymentDate"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.paymentDate} fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                          inputFormat="dd/MM/yyyy"
                          value={field.value}
                          onChange={(e) => field.onChange(e)}
                          renderInput={(params) => <TextField className="bg-white" fullWidth size="small" {...params} />}
                        />
                      </LocalizationProvider>
                      {errors.paymentDate ? <FormHelperText>{errors["paymentDate"]?.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            startIcon={<BTNLoader isLoading={isPending} />}
            variant="contained"
            color={"secondary"}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default UpdatePayment;
